import { queryKeys } from "@/constants/queryKeyFactory";
import { useMutation, useQuery } from "@/hooks/api";
import {
  ApiResponse,
  PaginationParams,
  QueryOptions,
} from "@/interfaces/api.interface";
import { IUser } from "@/interfaces/user.interface";
import { queryClient } from "@/lib/queryClient";
import authService from "@/services/auth.service";
import { userSvc } from "@/services/user.service";

export function useUsers(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IUser[]>>(
    queryKeys.users.list(params),
    () =>
      userSvc.get<IUser>({
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        sort: params.sort,
        filter: params.filter,
        searchFilters: params.searchFilters,
      }),
    options
  );
}

export function useUserById(id: string, options: QueryOptions = {}) {
  return useQuery<ApiResponse<IUser>>(
    queryKeys.users.detail(id),
    () => userSvc.getById<IUser>(id),
    {
      enabled: !!id,
      ...options,
    }
  );
}

export function useCreateUser(options: QueryOptions = {}) {
  return useMutation<ApiResponse<any>, any>(
    (data: IUser) => authService.createUser(data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.users.root,
        });
      },
    }
  );
}

export function useAllUsers(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IUser[]>>(
    queryKeys.users.all({ ...params }),
    () => userSvc.getAll<IUser>(params),
    options
  );
}

export function useUpdateUser(options: QueryOptions = {}) {
  return useMutation<ApiResponse<IUser>, { id: string; data: IUser }>(
    ({ id, data }) => userSvc.update<IUser>(id, data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.users.root,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.users.all,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.company.root,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.company.all,
        });
      },
    }
  );
}
